import { useEffect, useCallback, useRef, memo, FC } from "react";
import { Platform } from "react-native";
import { View, Text } from "@src/components";
import { calculateTotalDays, convertGridStateToRotatingRoutine, parseRotatingRoutineToGridState } from "./utils";
import { dateRecordToISOString, isoStringToDateRecord } from "../grid-utils";
import { useDragSelection } from "../../../hooks/useDragSelection";
import { useWebDragSelection } from "../../../hooks/useWebDragSelection";
import { GestureDetector } from "react-native-gesture-handler";
import { DayColumn } from "../DayColumn";
import { makeDateRecord, type DateRecord } from "@visioni/schedules";
import {
  type Granularity,
  DEFAULT_GRANULARITY,
  getSlotsPerDay,
  generateSlots,
  formatSlotTime,
  detectRoutineGranularity,
} from "../granularity-utils";
import { createGridStore, useGranularity, useNumDays, usePageOffset, type GridStore } from "../grid-store";
import { StyleSheet } from "react-native-unistyles";
import { RotatingAlarmRoutine } from "@visioni/a3s-alarm-schedules/schema";
import { CELL_HEIGHT, CELL_MARGIN_BOTTOM, CELL_TOTAL, MAX_VISIBLE_DAYS } from "../constants";

interface RotatingRoutineGridProps {
  onDataChange: (routine: RotatingAlarmRoutine) => void;
  initialData?: RotatingAlarmRoutine;
  store: GridStore;
}

const DEFAULT_NUM_DAYS = 4;

/**
 * Calculate the initial granularity for the grid based on the provided routine data.
 * If no initial data is provided, returns the default granularity of 60 minutes.
 */
function getInitialGranularity(initialData?: RotatingAlarmRoutine): Granularity {
  if (!initialData) return DEFAULT_GRANULARITY;
  return detectRoutineGranularity(initialData);
}

export function createRotatingStore(initialData?: RotatingAlarmRoutine): GridStore {
  const initialNumDays = initialData ? calculateTotalDays(initialData) : DEFAULT_NUM_DAYS;
  const initialStartDate = dateRecordToISOString(
    initialData?.start ?
      (initialData.start as DateRecord)
    : (() => {
        const now = new Date();
        return makeDateRecord(now.getFullYear(), now.getMonth() + 1, now.getDate());
      })(),
  );
  const initialGranularity = getInitialGranularity(initialData);
  return createGridStore(
    initialData ? parseRotatingRoutineToGridState(initialData, initialGranularity) : {},
    getSlotsPerDay(initialGranularity),
    initialGranularity,
    initialNumDays,
    initialStartDate,
  );
}

const RotatingDayHeaders = memo<{ store: GridStore; pageOffset: number }>(({ store, pageOffset }) => {
  const numDays = useNumDays(store);
  const visibleCount = Math.min(MAX_VISIBLE_DAYS, numDays - pageOffset);
  return (
    <View flexDirection="row" mb="md" alignItems="center">
      <View width={42} />
      {Array.from({ length: visibleCount }, (_, i) => (
        <View key={pageOffset + i} flex={1} alignItems="center" style={{ marginHorizontal: 2 }}>
          <Text textStyle="sm/regular" color="textMuted">
            {pageOffset + i + 1}
          </Text>
        </View>
      ))}
    </View>
  );
});

const RotatingDayColumns = memo<{
  store: GridStore;
  pageOffset: number;
  onPress: (day: number | string, slot: number) => void;
  registerCell: (day: number | string, hour: number, ref: any) => void;
}>(({ store, pageOffset, onPress, registerCell }) => {
  const numDays = useNumDays(store);
  const visibleCount = Math.min(MAX_VISIBLE_DAYS, numDays - pageOffset);
  return (
    <>
      {Array.from({ length: visibleCount }, (_, i) => (
        <DayColumn
          key={pageOffset + i}
          day={pageOffset + i}
          onPress={onPress}
          registerCell={registerCell}
          store={store}
        />
      ))}
    </>
  );
});

export const RotatingRoutineGrid: FC<RotatingRoutineGridProps> = memo(({ onDataChange, initialData, store }) => {
  const granularity = useGranularity(store) as Granularity;
  const numDays = useNumDays(store);
  const pageOffset = usePageOffset(store);
  const maxOffset = Math.max(0, numDays - MAX_VISIBLE_DAYS);

  // Clamp pageOffset when numDays shrinks
  useEffect(() => {
    if (pageOffset > maxOffset) {
      store.getState().setPageOffset(maxOffset);
    }
  }, [maxOffset, pageOffset, store]);

  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  // Subscribe to store changes — converts grid state to routine and notifies parent.
  // Runs outside React's render cycle so the grid doesn't re-render on cell toggles.
  // All values (cells, numDays, startDate, granularity) are read from the store.
  useEffect(() => {
    const convert = (state: ReturnType<typeof store.getState>) => {
      const routine = convertGridStateToRotatingRoutine(
        state.cells,
        state.numDays,
        isoStringToDateRecord(state.startDate),
        state.granularity as Granularity,
      );
      onDataChangeRef.current(routine);
    };
    convert(store.getState());
    return store.subscribe(convert);
  }, [store]);

  const handleDragSelection = useCallback(
    (selections: Array<{ day: number | string; hour: number }>) => {
      if (selections.length === 0) return;
      const { cells, batchUpdate } = store.getState();
      const firstKey = `${selections[0].day}-${selections[0].hour}`;
      const shouldSelect = !cells[firstKey];
      const updates: Record<string, boolean> = {};
      selections.forEach(({ day, hour }) => {
        updates[`${day}-${hour}`] = shouldSelect;
      });
      batchUpdate(updates);
    },
    [store],
  );

  // Use platform-specific drag selection hooks — drag state lives in the
  // Zustand store so individual cells subscribe to their own drag status.
  const mobileDragSelection = useDragSelection({
    onSelectionChange: handleDragSelection,
    store,
  });

  const getGridCells = useCallback(() => store.getState().cells, [store]);
  const webDragSelection = useWebDragSelection({
    onSelectionChange: handleDragSelection,
    getGridState: getGridCells,
    store,
  });

  const { registerCell, containerRef } = Platform.OS === "web" ? webDragSelection : mobileDragSelection;

  const panGesture = Platform.OS === "web" ? undefined : mobileDragSelection.panGesture;
  const containerProps = Platform.OS === "web" ? webDragSelection.getContainerProps() : {};

  const toggleCell = useCallback(
    (day: number | string, slot: number) => {
      store.getState().toggleCell(`${day}-${slot}`);
    },
    [store],
  );

  // Update grid state and start date when initialData changes
  useEffect(() => {
    if (initialData) {
      const currentGranularity = store.getState().granularity as Granularity;
      const requiredGranularity = detectRoutineGranularity(initialData);

      // Parse new initialData with the required granularity
      const newCells = parseRotatingRoutineToGridState(initialData, requiredGranularity);

      // If the new routine requires a different granularity, update it along with slotsPerDay
      if (requiredGranularity !== currentGranularity) {
        store.setState({
          granularity: requiredGranularity,
          slotsPerDay: getSlotsPerDay(requiredGranularity),
          cells: newCells,
          initialCells: newCells,
        });
      } else {
        store.setState({ cells: newCells, initialCells: newCells });
      }

      // Update start date if provided
      if (initialData.start) {
        store.getState().setStartDate(dateRecordToISOString(initialData.start as DateRecord));
      }
    }
  }, [initialData, store]);

  const renderGridContent = () => (
    <>
      <RotatingDayHeaders store={store} pageOffset={pageOffset} />

      <View flexDirection="row">
        <View width={42}>
          {generateSlots(granularity).map((slot) => (
            <View key={slot} style={styles.timeLabel} justifyContent="flex-start" alignItems="flex-end">
              <Text textStyle="xs/regular" color="textMuted" style={{ textAlign: "right" }}>
                {formatSlotTime(slot, granularity)}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[styles.gridRow, { height: getSlotsPerDay(granularity) * CELL_TOTAL, flex: 1, flexDirection: "row" }]}
        >
          <RotatingDayColumns store={store} pageOffset={pageOffset} onPress={toggleCell} registerCell={registerCell} />
        </View>
      </View>

      <View flexDirection="row" mt="xs">
        <View width={42} justifyContent="flex-start" alignItems="flex-end" style={{ paddingRight: 8 }}>
          <Text textStyle="xs/regular" color="textMuted" style={{ textAlign: "right", marginTop: -14 }}>
            {formatSlotTime(getSlotsPerDay(granularity), granularity)}
          </Text>
        </View>
        <View style={{ flex: 1 }} />
      </View>
    </>
  );

  return (
    <View pt="xl" width="100%">
      {Platform.OS === "web" ?
        <View {...containerProps}>{renderGridContent()}</View>
      : <GestureDetector gesture={panGesture!}>
          <View ref={containerRef as React.Ref<never>}>{renderGridContent()}</View>
        </GestureDetector>
      }
    </View>
  );
});

const styles = StyleSheet.create({
  gridRow: {
    overflow: "hidden",
  },
  timeLabel: {
    height: CELL_HEIGHT,
    marginBottom: CELL_MARGIN_BOTTOM,
    paddingRight: 8,
    top: -10,
  },
});
